import { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useLanguage } from '@/context/LanguageContext';
import LanguageSwitcher from '@/components/LanguageSwitcher';
import { TrendUp, ArrowLeft, Eye, Tag, ArrowRight, Calendar } from '@phosphor-icons/react';
import Logo from '@/components/Logo';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BlogPostPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    setLoading(true);
    setError(false);
    axios.get(`${API_URL}/blog/${slug}?lang=${language}`)
      .then(res => setPost(res.data))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [slug, language]);

  // SEO: update document metadata whenever post loads
  useEffect(() => {
    if (!post) return;
    document.title = `${post.title} — GrowthRIQ`;
    const setMeta = (key, keyValue, value) => {
      let el = document.head.querySelector(`meta[${key}="${keyValue}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(key, keyValue);
        document.head.appendChild(el);
      }
      el.setAttribute('content', value);
    };
    setMeta('name', 'description', post.meta_description || post.excerpt || '');
    setMeta('property', 'og:title', post.title);
    setMeta('property', 'og:description', post.meta_description || post.excerpt || '');
    setMeta('property', 'og:type', 'article');
    setMeta('property', 'og:locale', language === 'tr' ? 'tr_TR' : 'en_US');
    // OG image: use cover if present, else fall back to the dynamic generator.
    const apiBase = process.env.REACT_APP_BACKEND_URL;
    const ogImg = post.cover_image
      || `${apiBase}/api/og?title=${encodeURIComponent(post.title || 'GrowthRIQ')}&subtitle=${encodeURIComponent(post.excerpt || '').slice(0, 120)}&kind=blog`;
    setMeta('property', 'og:image', ogImg);
    setMeta('property', 'og:image:width', '1200');
    setMeta('property', 'og:image:height', '630');
    setMeta('name', 'twitter:image', ogImg);
    setMeta('name', 'twitter:card', 'summary_large_image');
    setMeta('name', 'twitter:title', post.title);
    setMeta('name', 'twitter:description', post.meta_description || post.excerpt || '');

    // Canonical URL — points to the language-specific blog post URL
    const origin = window.location.origin;
    const canonicalPath = language === 'en' ? `/en/blog/${slug}` : `/blog/${slug}`;
    let canonical = document.head.querySelector('link[rel="canonical"][data-blog-canonical="1"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      canonical.setAttribute('data-blog-canonical', '1');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', `${origin}${canonicalPath}`);

    // hreflang alternates (TR & EN)
    document.head.querySelectorAll('link[rel="alternate"][data-blog-alt="1"]').forEach((n) => n.remove());
    [['tr', `/blog/${slug}`], ['en', `/en/blog/${slug}`], ['x-default', `/blog/${slug}`]].forEach(([lang, p]) => {
      const a = document.createElement('link');
      a.setAttribute('rel', 'alternate');
      a.setAttribute('hreflang', lang);
      a.setAttribute('href', `${origin}${p}`);
      a.setAttribute('data-blog-alt', '1');
      document.head.appendChild(a);
    });

    // Article structured data (JSON-LD) for Google rich results
    document.head.querySelectorAll('script[data-ld]').forEach((n) => n.remove());

    const articleLd = document.createElement('script');
    articleLd.type = 'application/ld+json';
    articleLd.setAttribute('data-ld', 'article');
    articleLd.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Article",
      "headline": post.title,
      "description": post.meta_description || post.excerpt,
      "image": post.cover_image,
      "author": { "@type": "Organization", "name": post.author || "GrowthRIQ", "url": origin },
      "datePublished": post.published_at,
      "dateModified": post.updated_at || post.published_at,
      "mainEntityOfPage": { "@type": "WebPage", "@id": `${origin}${canonicalPath}` },
      "publisher": {
        "@type": "Organization",
        "name": "GrowthRIQ",
        "url": origin,
        "logo": { "@type": "ImageObject", "url": `${origin}/favicon.ico` },
      },
      "inLanguage": language === 'tr' ? 'tr-TR' : 'en-US',
      "keywords": (post.tags || []).join(', '),
    });
    document.head.appendChild(articleLd);

    // BreadcrumbList structured data (helps Google show breadcrumbs in results)
    const breadcrumbLd = document.createElement('script');
    breadcrumbLd.type = 'application/ld+json';
    breadcrumbLd.setAttribute('data-ld', 'breadcrumb');
    breadcrumbLd.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        { "@type": "ListItem", "position": 1, "name": "GrowthRIQ", "item": origin },
        { "@type": "ListItem", "position": 2, "name": language === 'tr' ? "Blog" : "Blog", "item": `${origin}${language === 'en' ? '/en/blog' : '/blog'}` },
        { "@type": "ListItem", "position": 3, "name": post.title, "item": `${origin}${canonicalPath}` },
      ],
    });
    document.head.appendChild(breadcrumbLd);

    document.documentElement.setAttribute('lang', language);
  }, [post, language, slug]);

  const prefix = language === 'en' ? '/en' : '';

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center text-[#52525B]" data-testid="post-loading">Loading…</div>;
  }

  if (error || !post) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 px-6" data-testid="post-error">
        <h1 className="heading-2 text-[#09090B]">
          {language === 'tr' ? 'Yazı bulunamadı' : 'Post not found'}
        </h1>
        <button onClick={() => navigate(`${prefix}/blog`)} className="btn-primary" data-testid="post-back-list">
          {language === 'tr' ? 'Tüm yazılara dön' : 'Back to all posts'}
        </button>
      </div>
    );
  }

  const publishedDate = new Date(post.published_at).toLocaleDateString(
    language === 'tr' ? 'tr-TR' : 'en-US',
    { year: 'numeric', month: 'long', day: 'numeric' }
  );

  return (
    <div className="min-h-screen bg-white">
      {/* Nav */}
      <nav className="border-b border-[#E4E4E7] sticky top-0 bg-white/95 backdrop-blur z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to={`${prefix}/`} className="flex items-center gap-2" data-testid="post-logo">
            <Logo variant="light" size="md" />
          </Link>
          <div className="flex items-center gap-4">
            <LanguageSwitcher />
            <Link to={`${prefix}/blog`} className="text-[#52525B] hover:text-[#09090B] font-medium hidden sm:inline" data-testid="post-nav-blog">
              {language === 'tr' ? 'Blog' : 'Blog'}
            </Link>
            <Link to={`${prefix}/register`} className="btn-primary" data-testid="post-nav-register">
              {t('nav.register')}
            </Link>
          </div>
        </div>
      </nav>

      {/* Back link */}
      <div className="max-w-3xl mx-auto px-6 pt-8">
        <Link to={`${prefix}/blog`} className="inline-flex items-center gap-1 text-sm text-[#52525B] hover:text-[#0F1B36] transition-colors" data-testid="post-back-link">
          <ArrowLeft size={14} weight="bold" />
          {language === 'tr' ? 'Tüm yazılar' : 'All posts'}
        </Link>
      </div>

      {/* Article */}
      <article className="max-w-3xl mx-auto px-6 py-8" data-testid="post-article">
        {/* Tags */}
        <div className="flex items-center gap-2 flex-wrap mb-4">
          {(post.tags || []).map(tag => (
            <span key={tag} className="text-xs px-2 py-1 bg-[#F4F4F5] text-[#52525B] uppercase tracking-wide" data-testid={`post-tag-${tag}`}>
              <Tag size={10} weight="bold" className="inline mr-1" />
              {tag}
            </span>
          ))}
        </div>

        {/* Title */}
        <h1 className="heading-1 text-[#09090B] mb-4" data-testid="post-title">{post.title}</h1>

        {/* Meta */}
        <div className="flex items-center gap-6 text-sm text-[#71717A] mb-8 pb-8 border-b border-[#E4E4E7]">
          <span>{post.author}</span>
          <span className="flex items-center gap-1">
            <Calendar size={14} weight="bold" /> {publishedDate}
          </span>
          <span className="flex items-center gap-1">
            <Eye size={14} weight="bold" /> {post.views} {language === 'tr' ? 'görüntülenme' : 'views'}
          </span>
        </div>

        {/* Cover image */}
        {post.cover_image && (
          <img
            src={post.cover_image}
            alt={post.title}
            className="w-full border border-[#E4E4E7] mb-8 aspect-video object-cover"
            width="1200"
            height="675"
            fetchPriority="high"
            loading="eager"
            decoding="async"
            data-testid="post-cover"
          />
        )}

        {/* Markdown content */}
        <div className="prose prose-lg max-w-none blog-content" data-testid="post-content">
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{post.content}</ReactMarkdown>
        </div>
      </article>

      {/* Inline CTA */}
      <section className="border-t border-[#E4E4E7] py-12 px-6" data-testid="post-cta-inline">
        <div className="max-w-3xl mx-auto bg-[#F4F4F5] border border-[#E4E4E7] p-8 text-center">
          <h3 className="heading-4 text-[#09090B] mb-2">
            {language === 'tr'
              ? 'Bu yazı ilgini çektiyse, kendi değerlendirmeni dene'
              : 'Enjoyed this? Try your own assessment'}
          </h3>
          <p className="text-[#52525B] mb-6">
            {language === 'tr'
              ? 'AI destekli analiz, SWOT raporu ve 90 günlük yol haritası — ücretsiz.'
              : 'AI-powered analysis, SWOT report, and a 90-day roadmap — free.'}
          </p>
          <Link to={`${prefix}/register`} className="btn-primary inline-flex items-center gap-2" data-testid="post-cta-register">
            {language === 'tr' ? 'Ücretsiz Başla' : 'Start Free'}
            <ArrowRight size={18} weight="bold" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default BlogPostPage;
